/*
  Button hover band

  The theme's buttons (snippets/button.liquid) run a hover animation: the content moves out as a band to the right while
  a copy comes in from the left, looping until the pointer leaves. The PDP add to cart tunes it so the whole band
  (text and bag icon) is copied and travels one button width (see "PDP add to cart" in custom.css).

  The buttons we built outside that snippet (offer cards, cart drawer, landing pages, quiz, header...) are plain
  <a>/<button> elements, so they had their own hovers or none. This script gives them the same band:
  - on the first hover a button's content is wrapped in a band, with a full copy of it just to its left;
  - the band moves one button width at 50px per second, linear and looping, like the add to cart;
  - on leave it eases back in 0.2s with the theme's curve.

  An element can run the band on a part of itself: hovering a mega menu image card or a homepage collection card moves
  only its label row, and a blog overview card only its "Read more": the element marked [data-hover-band-target].

  It only runs on devices with a fine pointer and without reduced motion, and leaves disabled or busy buttons alone.
  Wrapping happens on hover rather than on load, so buttons rendered later (cart drawer, quiz, re-rendered sections)
  work too, and a button whose text a script replaces is simply wrapped again on its next hover.
*/

const SELECTOR = [
  '.cart-offer-card__button',
  '.cart-drawer__checkout',
  '.cart-cross-sell-card__button',
  '.quantity-offer__choose',
  '.story-cards__button',
  '.landing-button',
  '.landing-hero__topbar-button',
  '.landing-offers__button',
  '.skin-quiz__cta',
  '.page-banner__button',
  '.instagram-feed__button',
  '.coco-club-dashboard__button',
  '.split-cards__submit',
  '.header__button',
  '.menu-drawer__button',
  '.mega-menu-card__button',
  '.mega-menu-image-card',
  '.collection-list__item:has([data-hover-band-target])',
  '.blog-overview-card',
  '#shopify-pc__banner__btn-accept',
  '#shopify-pc__banner__btn-manage-prefs',
  '.cookie-decline-button #shopify-pc__banner__btn-decline',
  '#shopify-pc__prefs__header-save'
].join(',');

const PIXELS_PER_SECOND = 50;
const LEAVE_DURATION = 200;
const LEAVE_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

const pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

const loops = new WeakMap();

function isInactive(button) {
  return button.matches(':disabled, [aria-disabled="true"], [aria-busy="true"]');
}

/* Wrap the content once; a later script rewriting the button removes the band, and the next hover wraps it again */
function wrap(button) {
  const existing = button.querySelector(':scope > .hover-band');

  if (existing) {
    return existing;
  }

  const style = getComputedStyle(button);
  const isFlex = style.display.includes('flex');
  const paddingStart = style.paddingInlineStart;
  const paddingEnd = style.paddingInlineEnd;

  const band = document.createElement('span');
  band.className = 'hover-band';
  band.style.marginInline = `calc(-1 * ${paddingStart}) calc(-1 * ${paddingEnd})`;
  band.style.width = `calc(100% + ${paddingStart} + ${paddingEnd})`;

  const item = document.createElement('span');
  item.className = 'hover-band__item';
  item.style.display = isFlex ? 'flex' : 'block';
  item.style.paddingInline = `${paddingStart} ${paddingEnd}`;

  if (isFlex) {
    item.style.flexDirection = style.flexDirection;
    item.style.justifyContent = style.justifyContent;
    item.style.alignItems = style.alignItems;
    item.style.gap = `${style.rowGap} ${style.columnGap}`;
  }

  while (button.firstChild) {
    item.appendChild(button.firstChild);
  }

  band.appendChild(item);
  button.appendChild(band);
  button.setAttribute('data-hover-band', '');

  return band;
}

/* The copy is rebuilt on every hover so it always matches the current content (labels can change) */
function refreshCopy(band) {
  band.querySelector(':scope > .hover-band__copy')?.remove();

  const copy = band.querySelector(':scope > .hover-band__item').cloneNode(true);
  copy.classList.add('hover-band__copy');
  copy.setAttribute('aria-hidden', 'true');
  copy.removeAttribute('id');
  copy.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

  band.appendChild(copy);
}

/* The element whose content moves: a marked target inside the hovered element, or the element itself */
function targetOf(trigger) {
  return trigger.querySelector('[data-hover-band-target]') || trigger;
}

function start(trigger) {
  if (isInactive(trigger)) {
    return;
  }

  const button = targetOf(trigger);
  const band = wrap(button);
  refreshCopy(band);

  loops.get(band)?.cancel();

  const direction = getComputedStyle(button).direction === 'rtl' ? -1 : 1;
  const duration = (band.offsetWidth / PIXELS_PER_SECOND) * 1000;

  const loop = band.animate(
    [{ transform: 'translateX(0)' }, { transform: `translateX(${direction * 100}%)` }],
    { duration, iterations: Infinity, easing: 'linear' }
  );

  loops.set(band, loop);
}

function finish(trigger) {
  const band = targetOf(trigger).querySelector(':scope > .hover-band');
  const loop = band && loops.get(band);

  if (!loop) {
    return;
  }

  const current = getComputedStyle(band).transform;
  loop.cancel();
  loops.delete(band);

  band.animate(
    [{ transform: current === 'none' ? 'translateX(0)' : current }, { transform: 'translateX(0)' }],
    { duration: LEAVE_DURATION, easing: LEAVE_EASING }
  );
}

document.addEventListener('pointerover', (event) => {
  if (!pointerQuery.matches || motionQuery.matches) {
    return;
  }

  const button = event.target.closest?.(SELECTOR);

  if (button && !button.contains(event.relatedTarget)) {
    start(button);
  }
});

document.addEventListener('pointerout', (event) => {
  const button = event.target.closest?.(SELECTOR);

  if (button && !button.contains(event.relatedTarget)) {
    finish(button);
  }
});
